/**
 * SettingsPanel.tsx
 * 
 * A floating settings gear button that opens a glassmorphic panel 
 * allowing users to customize UI elements like the scroll progress bar style and color.
 * 
 * This creates a premium, interactive customization experience
 * that makes the portfolio feel more personal and engaging.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// -- Progress Bar Style Options --
// Each style has a unique visual treatment
export type ProgressBarStyle = 'lightning' | 'normal' | 'binary' | 'fiber';

// -- Color Palette Options --
// Curated colors that look premium on dark backgrounds
export type ProgressBarColor = 'neon-emerald' | 'cyber-blue' | 'plasma-purple' | 'ember-orange' | 'aurora' | 'ghost-white';

// Human-readable labels and CSS values for each color
const COLOR_OPTIONS: Record<ProgressBarColor, { label: string; gradient: string; glow: string }> = {
    'neon-emerald': {
        label: 'Neon Emerald',
        gradient: 'linear-gradient(90deg, #10b981, #34d399, #6ee7b7)',
        glow: 'rgba(16, 185, 129, 0.6)',
    },
    'cyber-blue': {
        label: 'Cyber Blue',
        gradient: 'linear-gradient(90deg, #0ea5e9, #38bdf8, #7dd3fc)',
        glow: 'rgba(14, 165, 233, 0.6)',
    },
    'plasma-purple': {
        label: 'Plasma Purple',
        gradient: 'linear-gradient(90deg, #a855f7, #c084fc, #e879f9)',
        glow: 'rgba(168, 85, 247, 0.6)',
    },
    'ember-orange': {
        label: 'Ember Orange',
        gradient: 'linear-gradient(90deg, #f97316, #fb923c, #fdba74)',
        glow: 'rgba(249, 115, 22, 0.6)',
    },
    'aurora': {
        label: 'Aurora',
        gradient: 'linear-gradient(90deg, #10b981, #0ea5e9, #a855f7, #f43f5e)',
        glow: 'rgba(14, 165, 233, 0.4)',
    },
    'ghost-white': {
        label: 'Ghost White',
        gradient: 'linear-gradient(90deg, rgba(255,255,255,0.6), rgba(255,255,255,0.9), rgba(255,255,255,0.6))',
        glow: 'rgba(255, 255, 255, 0.3)',
    },
};

// Style labels
const STYLE_OPTIONS: Record<ProgressBarStyle, { label: string; icon: string }> = {
    'lightning': { label: '⚡ Lightning', icon: '⚡' },
    'normal': { label: '━ Smooth', icon: '━' },
    'binary': { label: '01 Binary', icon: '01' },
    'fiber': { label: '◉ Fiber', icon: '◉' },
};

interface SettingsPanelProps {
    progressStyle: ProgressBarStyle;
    progressColor: ProgressBarColor;
    onStyleChange: (style: ProgressBarStyle) => void;
    onColorChange: (color: ProgressBarColor) => void;
}

export function SettingsPanel({ progressStyle, progressColor, onStyleChange, onColorChange }: SettingsPanelProps) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            {/* Gear Button - fixed bottom-right */}
            <motion.button
                className="fixed bottom-6 right-6 z-[200] w-12 h-12 rounded-full bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center text-gray-400 hover:text-emerald-400 hover:border-emerald-500/30 transition-all shadow-lg"
                onClick={() => setIsOpen(!isOpen)}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Toggle settings panel"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
            </motion.button>

            {/* Settings Panel */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: 100, scale: 0.8 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 100, scale: 0.8 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed bottom-20 right-6 z-[200] w-72 bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl p-5 shadow-2xl"
                    >
                        {/* Title */}
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">UI Settings</h3>
                            <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-white transition-colors text-xs">✕</button>
                        </div>

                        {/* Progress Bar Section */}
                        <div className="mb-5">
                            <p className="text-[10px] font-mono text-emerald-500/70 tracking-widest uppercase mb-2">Progress Bar Style</p>
                            <div className="grid grid-cols-2 gap-2">
                                {(Object.keys(STYLE_OPTIONS) as ProgressBarStyle[]).map((style) => (
                                    <button
                                        key={style}
                                        onClick={() => onStyleChange(style)}
                                        className={`px-3 py-2 rounded-lg text-xs font-mono transition-all border ${progressStyle === style
                                            ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                                            : 'bg-white/5 border-white/5 text-gray-400 hover:border-white/20 hover:text-white'
                                            }`}
                                    >
                                        {STYLE_OPTIONS[style].label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Color Section */}
                        <div>
                            <p className="text-[10px] font-mono text-emerald-500/70 tracking-widest uppercase mb-2">Progress Bar Color</p>
                            <div className="grid grid-cols-3 gap-2">
                                {(Object.keys(COLOR_OPTIONS) as ProgressBarColor[]).map((color) => (
                                    <button
                                        key={color}
                                        onClick={() => onColorChange(color)}
                                        className={`relative px-2 py-2 rounded-lg text-[10px] font-mono transition-all border ${progressColor === color
                                            ? 'border-white/40 text-white'
                                            : 'border-white/5 text-gray-500 hover:border-white/20 hover:text-gray-300'
                                            }`}
                                    >
                                        {/* Color preview swatch */}
                                        <div
                                            className="w-full h-1.5 rounded-full mb-1.5"
                                            style={{ background: COLOR_OPTIONS[color].gradient }}
                                        />
                                        {COLOR_OPTIONS[color].label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Preview */}
                        <div className="mt-4 pt-3 border-t border-white/5">
                            <p className="text-[9px] font-mono text-gray-600 tracking-widest uppercase mb-2">Live Preview</p>
                            <div className="h-2 rounded-full overflow-hidden bg-white/5">
                                <div
                                    className="h-full w-3/4 rounded-full"
                                    style={{ background: COLOR_OPTIONS[progressColor].gradient }}
                                />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

// Export constants for use in App.tsx
export { COLOR_OPTIONS, STYLE_OPTIONS };
